import { ReactComponent as TrashIcon } from 'assets/trash-icon.svg';
import classnames from 'classnames';
import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { UncontrolledTooltip } from 'reactstrap';
import {
  MAX_ALLOWED_BTNS,
  MAX_ALLOWED_QQ_TEXT_LENGTH,
} from '@mm/constants/widgets';
import {
  SAVE_TO_ATTR,
  TYPES,
  TYPES_ARRAY,
} from '../../../../editor/features/build/components/widgets/constants/quickQuestion';
import { makeButton } from '../../../../editor/features/build/requestModels/widgets/buttonStackWidget/Button';
import { useButtonCRUDActions } from '@mm/state/actions/ButtonCRUDActions';
import ButtonModel, { ButtonType } from '@mm/state/model/ButtonModel';
import { ChatbotChannel } from '@mm/state/model/ChatbotModel';
import { FormEntry } from '@mm/state/model/FormsModel';
import WidgetModel from '@mm/state/model/WidgetModel';
import DeleteCircleButton from '../../../buttons/DeleteCircleButton/DeleteCircleButton';
import {
  CollapsibleItem,
  CollapsibleItemBody,
  CollapsibleItemHeader,
} from '../../../components/CollapsibleItem/CollapsibleItem';
import { OnClickElsewhere } from '../../../components/OnClickElsewhere/OnClickElsewhere';
import PrimarySelect, {
  DropdownOption,
} from '../../../components/PrimarySelect/PrimarySelect';
import SingleAttributeInput from '../../../components/SingleAttributeInput/SingleAttributeInput';
import { timeout } from '../../../helpers/utils';
import { SortableElement } from '../../../lib/react-sortable-hoc';
import '../../../scss/input.scss';
import { BubbleTextInput } from '../../common/BubbleTextInput/BubbleTextInput';
import { FormWidgetHelpers } from '../form-helpers';
import { Alert } from '@mm/components';
import './FormField.scss';

type Props = {
  ref: React.RefObject<HTMLDivElement>;
  widget: WidgetModel;
  field: FormEntry;
  validationOptions: string[];
  error: string;
  onUpdate: (field: FormEntry) => Promise<FormEntry>;
  onDelete: () => void;
  showAttributePicker?: boolean;
  showOnlyNameAttributes?: boolean;
  questionNumber: number;
  restrictToChannels?: ChatbotChannel[];
  showCountByChannel?: boolean;
  usedInSlideout?: boolean;
};

const FormFieldBase = (props: Props) => {
  const {
    field,
    widget,
    field: { validationRule: initValidationRule, id, buttons },
    validationOptions,
    error,
    onUpdate,
    onDelete,
    ref,
    showAttributePicker = true,
    showOnlyNameAttributes = false,
    questionNumber,
    restrictToChannels,
    showCountByChannel,
    usedInSlideout,
  } = props;

  const buttonCRUDActions = useButtonCRUDActions();

  const [body, setBody] = useState(field.body);
  const [response, setResponse] = useState(field.response);
  const [valueInput, setValueInput] = useState('');
  const [type, setType] = useState(FormWidgetHelpers.getType(field));
  const [typeOption, setTypeOptions] = useState<DropdownOption>(
    TYPES_ARRAY.find(t => t.name === type!) || TYPES_ARRAY[0],
  );
  const [hasAttributesInBody, setHasAttributesInBody] = useState(false);

  useEffect(() => {
    setHasAttributesInBody(/{{.+}}/.test(body));
  }, [body]);

  const mappedValidationOptions = useMemo(
    () => [
      { id: 0, name: 'No Validation', value: '' },
      ...validationOptions.map((o, idx) => ({
        id: idx + 1,
        name: `${o.slice(0, 1).toUpperCase()}${o.slice(1)}`,
        value: o,
      })),
    ],
    [validationOptions],
  );

  const [isQuestionEdit, setIsQuestionEdit] = useState(false);

  const [showResponse, setShowResponse] = useState(!!response);
  const [isResponseEdit, setIsResponseEdit] = useState(false);
  const [isValueEdit, setIsValueEdit] = useState(type === TYPES.MULTIPLE);
  const [validationRule, setValidationRule] = useState<DropdownOption>(
    mappedValidationOptions.find(o => o.value === initValidationRule) ||
      mappedValidationOptions[0],
  );

  useEffect(() => {
    const newValidationRule =
      mappedValidationOptions.find(o => o.value === initValidationRule) ||
      mappedValidationOptions[0];

    setValidationRule(newValidationRule);
  }, [initValidationRule]);

  const updateQuestionBody = (newText: string) => {
    if (body !== newText) {
      setBody(newText);
      const updatedWidget = _.set(_.cloneDeep(field), 'body', newText);
      onUpdate(updatedWidget);
    }
  };

  const onTypeChange = useCallback(
    (value: boolean) => {
      (async () => {
        const formEntry = await removeAllButtons();

        if (value !== formEntry.allowFreeTextResponse) {
          const formEntryUpdate = _.set(
            _.cloneDeep(formEntry),
            'allowFreeTextResponse',
            value,
          );
          onUpdate(formEntryUpdate);
        }
      })();
    },
    [field.buttons.length],
  );

  const removeAllButtons = async () => {
    const copy = _.cloneDeep(field);
    copy.buttons = [];
    copy.validationRule = '';
    // Note: need to await here to help fight race conditions.
    const res: FormEntry = await onUpdate(copy);
    await timeout(1500);
    return res;
  };

  const addPhoneButton = async () => {
    await removeAllButtons();
    const maxSortOrder = Math.max(...buttons.map(b => b.sortOrder), 0);
    const newButton = makeButton({
      type: ButtonType.PHONE_NUMBER,
      name: '',
      sortOrder: maxSortOrder + 1,
      id: null,
    });

    buttonCRUDActions.createEntry(widget.id, field.id, newButton);
  };

  const addEmailButton = async () => {
    await removeAllButtons();
    const maxSortOrder = Math.max(...buttons.map(b => b.sortOrder), 0);
    const newButton = makeButton({
      type: ButtonType.EMAIL,
      name: '',
      sortOrder: maxSortOrder + 1,
      id: null,
    });

    buttonCRUDActions.createEntry(widget.id, field.id, newButton);
  };

  const onChangeType = async (type: string) => {
    switch (type) {
      case TYPES.MULTIPLE: {
        setType(type);
        onTypeChange(false);
        return setIsValueEdit(true);
      }

      case TYPES.TEXT: {
        setType(type);
        return onTypeChange(true);
      }

      case TYPES.PHONE: {
        setType(type);
        return addPhoneButton();
      }

      case TYPES.EMAIL: {
        setType(type);
        return addEmailButton();
      }

      default: {
        return null;
      }
    }
  };

  const onValidationChange = ({ value }: typeof mappedValidationOptions[0]) => {
    if (value !== field.type) {
      const updatedField = _.set(_.cloneDeep(field), 'validationRule', value);
      onUpdate(updatedField);
    }
  };

  const updateResponse = (newText: string) => {
    setIsResponseEdit(false);
    if (response !== newText) {
      const updatedField = _.set(_.cloneDeep(field), 'response', newText);
      onUpdate(updatedField);
      setResponse(newText);
    }
    setShowResponse(!!newText);
  };

  const onValueAdd = (value: string) => {
    setValueInput(value);
    const { buttons = [] } = field;
    if (buttons.length < MAX_ALLOWED_BTNS) {
      const newValue = makeButton({
        type: ButtonType.POSTBACK,
        name: value,
        sortOrder: buttons.length,
        configExtendObject: {
          payload: '',
          assetId: undefined,
        },
        id: null,
      });

      buttonCRUDActions.createEntry(widget.id, field.id, newValue);
    }

    setTimeout(() => setValueInput(''), 100);
  };

  const onValueDelete = (button: ButtonModel) => {
    buttonCRUDActions.deleteEntry(widget.id, field.id, button);
  };

  const tooltipId = `form-field-${id}`;
  const isValid = !error;
  const shouldRenderValidation = type === TYPES.TEXT;
  const tooltipClassList = classnames(
    'fa',
    'fa-exclamation-circle',
    'fa-lg',
    'widget-item__error',
    {
      'widget-item__error--hidden': isValid,
    },
  );

  const handleQuestionClickOutside = useCallback(
    () => setIsQuestionEdit(false),
    [setIsQuestionEdit],
  );

  const handleValueClickOutside = useCallback(
    () => setIsValueEdit(false),
    [setIsValueEdit],
  );

  const handleResponseClickOutside = useCallback(() => {
    setIsResponseEdit(false);
    if (!response) setShowResponse(false);
  }, [setIsResponseEdit, setShowResponse, response]);

  return (
    <CollapsibleItem
      render={() => (
        <div ref={ref}>
          <CollapsibleItemHeader
            title={`Question ${questionNumber}`}
            className="form-field__header"
          >
            <i id={tooltipId} className={tooltipClassList} />
            <DeleteCircleButton onClick={onDelete} />
          </CollapsibleItemHeader>
          <CollapsibleItemBody>
            <div className="form-field">
              <OnClickElsewhere
                className="form-field__name-wrapper"
                handleClick={handleQuestionClickOutside}
                id={`form-field-${field.id}-body`}
              >
                <BubbleTextInput
                  className="form-field__name"
                  editMode={isQuestionEdit}
                  onClick={() => setIsQuestionEdit(true)}
                  value={body}
                  updateText={updateQuestionBody}
                  placeholder={'Question or prompt'}
                  showAttributePicker={
                    questionNumber !== 1 || showAttributePicker
                  }
                  showOnlyNameAttributes={showOnlyNameAttributes}
                  newCharacterCountDesign
                  showCountByChannel={showCountByChannel}
                  maxSmsChannelTextLength={MAX_ALLOWED_QQ_TEXT_LENGTH}
                  maxOthersChannelTextLength={MAX_ALLOWED_QQ_TEXT_LENGTH}
                  setEditMode={setIsQuestionEdit}
                />
              </OnClickElsewhere>

              {hasAttributesInBody &&
                questionNumber === 1 &&
                !showAttributePicker && (
                  <Alert
                    className="form-widget__warning"
                    type={'error'}
                    showCloseIcon={false}
                  >
                    Attributes are not available on the first widget since we
                    only get contact’s profile AFTER their first response.
                  </Alert>
                )}

              {type === TYPES.MULTIPLE && (
                <div className="form-field__container form-field__container--multiple">
                  <div className="form-field__heading">Response Value:</div>
                  <OnClickElsewhere
                    className="form-field__name-wrapper--white"
                    handleClick={handleValueClickOutside}
                    id={`form-field-${field.id}-value`}
                  >
                    <BubbleTextInput
                      className="form-field__name"
                      editMode={isValueEdit}
                      onClick={() => setIsValueEdit(true)}
                      value={valueInput}
                      updateText={onValueAdd}
                      showAttributePicker={showAttributePicker}
                      disabled={buttons.length >= MAX_ALLOWED_BTNS}
                      multiline={false}
                      saveOnKeyboard
                      showTextLength={false}
                    />
                  </OnClickElsewhere>
                  {buttons.length < MAX_ALLOWED_BTNS ? null : (
                    <UncontrolledTooltip
                      className="form-field__tooltip"
                      innerClassName="form-field__inner-tooltip"
                      target={`form-field-${id}-value`}
                    >
                      Reached limit of {MAX_ALLOWED_BTNS} options
                    </UncontrolledTooltip>
                  )}
                  <div className="form-field__values-cloud">
                    {buttons.map((b, i) => (
                      <div key={b.id} className="form-field__value-item">
                        <div>{b.name}</div>

                        <div
                          className="form-field__value--plus-icon form-field__value--icon--idle"
                          onClick={e => {
                            e.stopPropagation();
                            onValueDelete(b);
                          }}
                        >
                          +
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="form-field__wrapper--right-aligned">
                <div className="form-field__wrapper--right-aligned--column">
                  <div className="form-field__reply-container">
                    <div className="form-field__reply-label">
                      User replies with
                    </div>
                    <div className="form-field__reply-wrapper">
                      <div className="form-field__left-container">
                        <div className="form-field__input-container">
                          <PrimarySelect
                            options={TYPES_ARRAY}
                            value={typeOption}
                            onChange={(selectedType: DropdownOption) => {
                              setTypeOptions(selectedType);
                              onChangeType(selectedType.name!);
                            }}
                            className="question-reply-type__select"
                            customDropdownStyles={{
                              zIndex: usedInSlideout ? '1000003' : '999',
                            }}
                          />
                        </div>
                      </div>
                      {shouldRenderValidation && (
                        <div className="form-field__right-container">
                          <div className="form-field__input-container">
                            <PrimarySelect
                              options={mappedValidationOptions}
                              value={validationRule}
                              onChange={(
                                rule: typeof mappedValidationOptions[0],
                              ) => {
                                setValidationRule(rule);
                                onValidationChange(rule);
                              }}
                              className="question-reply-type__select"
                              customDropdownStyles={{
                                zIndex: usedInSlideout ? '1000003' : '999',
                              }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="form-field__container form-field__container--column">
                    <p className="form-field__heading">{SAVE_TO_ATTR}</p>
                    <div className="form-field__input-container chatbot__input-group">
                      <SingleAttributeInput
                        formEntry={field}
                        widget={widget}
                        qqResponseType={type}
                        className="form-field__add-attribute"
                        usedInSlideout={usedInSlideout}
                      />
                    </div>
                  </div>
                </div>
              </div>
              {showResponse && (
                <div
                  className={classnames(
                    'form-field__name-wrapper--white',
                    'form-field__response',
                  )}
                >
                  <div className="form-field__header-row">
                    <div className="form-field__heading">
                      Quick Response (Optional):
                    </div>

                    <TrashIcon
                      onClick={() => updateResponse('')}
                      className="form-field__trash-icon"
                    />
                  </div>
                  <OnClickElsewhere
                    handleClick={handleResponseClickOutside}
                    id={`form-field-${field.id}-response`}
                  >
                    <BubbleTextInput
                      className="form-field__name"
                      editMode={isResponseEdit}
                      onClick={() => setIsResponseEdit(true)}
                      value={response}
                      updateText={updateResponse}
                      showTextLength={false}
                      showOnlyNameAttributes={showOnlyNameAttributes}
                      multiline={false}
                      orientation="horizontal"
                      emojiPickerColor="#9a9aa2"
                    />
                  </OnClickElsewhere>
                </div>
              )}
              <UncontrolledTooltip
                className="form-field__tooltip"
                innerClassName="form-field__inner-tooltip"
                target={tooltipId}
              >
                {error}
              </UncontrolledTooltip>
            </div>
            {!showResponse && (
              <div
                className="form__add-link"
                onClick={() => setShowResponse(true)}
              >
                + Add Quick Response (optional)
              </div>
            )}
          </CollapsibleItemBody>
        </div>
      )}
    />
  );
};

export const FormFieldComponent = SortableElement(
  ({
    itemRef,
    ...props
  }: Props & { itemRef: React.RefObject<HTMLDivElement> }) => (
    <FormFieldBase {...props} ref={itemRef || props.ref} />
  ),
);
